import React, { useState, useEffect, useRef, Suspense, memo } from 'react';
import "./App.css";
import { Document, Page, pdfjs } from 'react-pdf';
import ReactVideoPlayer from './react-player';
import HeicViewer from './heic-viewer';
import CSVViewer from './renderCSVPreview';
import pdfIcon from './icons/pdf.png';
import pptIcon from './icons/ppt.png';
import docIcon from './icons/doc.png';
import mp4Icon from './icons/mp4-file.png';
import mp3Icon from './icons/mp3.png';
import psdIcon from './icons/photoshop.png';
import csvIcon from './icons/csv.png';
import movIcon from './icons/mov.png';
import defaultIcon from './icons/word.png';
import xlsIcon from './icons/xls.png';
import heicIcon from './icons/heic.png';

const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];

const getAttachmentIcon = (attachment) => {
    const extension = attachment.split('?')[0].split('#')[0].split('.').pop().toLowerCase();
    if (imageExtensions.includes(extension)) {
        return attachment; // Return the image URL if it's an image file
    }
    switch (extension) {
        case 'pdf':
            return pdfIcon;
        case 'ppt':
        case 'pptx':
            return pptIcon;
        case 'mp4':
            return mp4Icon;
        case 'mp3':
            return mp3Icon;
        case 'psd':
            return psdIcon;
        case 'csv':
            return csvIcon;
        case 'mov':
            return movIcon;
        case 'xls':
        case 'xlsx': // Corrected xlxs to xlsx
            return xlsIcon;
        case 'doc':
        case 'docx':
            return docIcon;
        case 'heic':
            return heicIcon;
        default:
            return defaultIcon;
    }
};

// Memoized File Viewer Component
const FileViewer = memo(({ attachment }) => {
    const fileExtension = attachment.split('?')[0].split('#')[0].split('.').pop().toLowerCase();

    switch (fileExtension) {
        case 'pdf':
            return (
                <Suspense fallback={<div>Loading PDF...</div>}>
                    <iframe className="file-viewer1" src={attachment} type="application/pdf" />
                </Suspense>
            );
        case 'doc':
        case 'docx':
            return (
                <Suspense fallback={<div>Loading Word Document...</div>}>
                    <iframe
                        className="file-viewer1"
                        src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(attachment)}`}
                        title="Document Viewer"
                    />
                </Suspense>
            );
        case 'ppt':
        case 'pptx':
            return (
                <Suspense fallback={<div>Loading PowerPoint...</div>}>
                    <iframe
                        className="file-viewer1"
                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(attachment)}`}
                        title="PowerPoint Viewer"
                    />
                </Suspense>
            );
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
        case 'webp':
            return <img className="image1" src={attachment} alt="Preview" loading="lazy" />;
        case 'mp3':
        case 'wav':
            return (
                <audio controls className="file-viewer">
                    <source src={attachment} type={`audio/${fileExtension}`} />
                    Your browser does not support the audio tag.
                </audio>
            );
        case 'mp4':
        case 'webm':
        case 'mov':
            return <ReactVideoPlayer url={attachment} controls={true} />;
        case 'xls':
        case 'xlsx':
            return (
                <Suspense fallback={<div>Loading Excel Document...</div>}>
                    <iframe
                        className="file-viewer1"
                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(attachment)}`}
                        title="Excel Viewer"
                    />
                </Suspense>
            );
        case 'heic':
            return <HeicViewer selectedDocument={attachment} />;
        case 'psd':
            return (
                <Suspense fallback={<div>Loading PSD...</div>}>
                    <iframe
                        className="file-viewer1"
                        src={`https://pixlr.com/e/?image=${encodeURIComponent(attachment)}`}
                        title="PSD Viewer"
                    />
                </Suspense>
            );
        case 'txt':
            return (
                <embed className="file-viewer1" src={attachment} type="text/plain" />
            );
        case 'csv':
            return <CSVViewer attachment={attachment} />;
        default:
            return (
                <div className="unsupported-file-slider">
                    <img
                        className="attachment-image cursor-pointer"
                        src={getAttachmentIcon(attachment)}
                        alt="Attachment"
                    />
                </div>
            );
    }
});

const LazyFileViewer = ({ attachment }) => {
    const [isVisible, setIsVisible] = useState(false);
    const observerRef = useRef();

    useEffect(() => {
        observerRef.current = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (entry.isIntersecting) {
                setIsVisible(true);
                observerRef.current.disconnect();
            }
        });

        // Encode the attachment URL to create a valid ID
        const safeId = `attachment-${encodeURIComponent(attachment)}`;

        const targetElement = document.getElementById(safeId);
        if (targetElement) {
            observerRef.current.observe(targetElement);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [attachment]);

    // Use the encoded ID for the div
    const safeId = `attachment-${encodeURIComponent(attachment)}`;

    return (
        <div id={safeId} style={{ height: '100%' }}>
            {isVisible ? <FileViewer attachment={attachment} /> : <div>Loading...</div>}
        </div>
    );
};

export default LazyFileViewer;
